/**
 * CREATE-BANK-QUESTION-MODAL
 * "Ajouter une question à la banque" (/supervisor/benchmark, "Banque de questions") — moved from
 * an inline expanding text input next to the search bar into its own modal (2026-09-14, Tom).
 *
 * Language picker rather than auto-detection (2026-09-14) — unreliable on text this short (a
 * detector needs more words to be confident, and fr/es/en especially blur together on one
 * sentence), and the supervisor typing it already knows which language it's in.
 */

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { useAppDispatch } from "@/core/root-reducer"
import Modal from "@/components/modal"
import Button from "@/components/button"
import TextInput from "@/components/text-input"
import ListDropdown from "@/components/list-dropdown"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import {
  createBankQuestion_AsSupervisor,
  calculateOneBankQuestion_AsSupervisor
} from "@/features/benchmark/entities/question/model/bankQuestion.thunk"
import {
  BANK_QUESTION_LANGUAGES,
  BANK_QUESTION_LANGUAGE_LABELS,
  BANK_QUESTION_LANGUAGE_FLAGS
} from "@/features/benchmark/entities/question/model/bankQuestionLanguages"

interface Props {
  onClose: () => void
  onCreated: () => void //Refetch the list after a successful create
}

function languageLabel(lang: string) {
  return `${BANK_QUESTION_LANGUAGE_FLAGS[lang]} ${BANK_QUESTION_LANGUAGE_LABELS[lang]}`
}

function CreateBankQuestionModal(props: Props) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [text, setText] = useState("")
  const [lang, setLang] = useState("fr")

  async function handleCreate() {
    const value = text.trim()
    if (!value) return

    const res = await dispatch(createBankQuestion_AsSupervisor({ text: value, lang })).unwrap()
    if (res.error || !res.id) return // fetchBackend already toasted the real error

    toast(t(res.alreadyExists ? "benchmark_editor_supervisor_already_in_bank_toast" : "benchmark_editor_supervisor_created_toast"))
    // Only legitimate for fr (2026-09-18, Tom: "s'il est légitime oui") — syncBankIdFromTexts only
    // ever compares the FR translation of an archived question, so a calculate right after
    // creating an en/de/es-only row is a guaranteed no-op, nothing to link yet.
    if (lang === "fr") await dispatch(calculateOneBankQuestion_AsSupervisor(res.id)).unwrap()
    props.onCreated()
    props.onClose()
  }

  return (
    <Modal onClose={props.onClose} isCloseButtonVisible isLarge title={t("benchmark_editor_supervisor_create_standalone")}>
      <div style={{ marginBottom: 16, maxWidth: 200 }}>
        <ListDropdown
          value={languageLabel(lang)}
          values={BANK_QUESTION_LANGUAGES.map((l) => ({ id: l, name: languageLabel(l) }))}
          onSelect={(e) => setLang(e.id)}
        />
      </div>
      <TextInput
        isFullWidth
        value={text}
        onChange={(e) => setText(e.value)}
        onKeyDown={(key) => key === "Enter" && handleCreate()}
        placeholder={t("benchmark_editor_supervisor_create_placeholder")}
        autoFocus
      />
      <div style={{ marginTop: 16 }}>
        <Button icon={faPlus} className="primary" isFullWidth onClick={handleCreate}>
          {t("benchmark_editor_supervisor_create")}
        </Button>
      </div>
    </Modal>
  )
}

export default CreateBankQuestionModal
